import { useEffect } from 'react';
import './GlaiveRushAnimation.css';

/**
 * PROFESSIONAL GLAIVE RUSH ANIMATION
 * Dragon-type physical move - reckless charge with draconic energy
 * 
 * Key Features:
 * - Dragon-type physical attack (120 base power, 100% accuracy)
 * - User takes double damage next turn
 * - Baxcalibur signature move with icy dragon charge
 * - Professional gaming quality with draconic energy and reckless impact
 */
export default function GlaiveRushAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Dragon Energy Buildup (0-450ms)
    playerEl.classList.add('glaive-rush-user-charge');

    const dragonAura = document.createElement('div');
    dragonAura.className = 'glaive-rush-dragon-aura';
    dragonAura.style.left = `${playerX - 75}px`;
    dragonAura.style.top = `${playerY - 75}px`;
    container.appendChild(dragonAura);
    elements.push(dragonAura);

    for (let i = 0; i < 10; i++) {
      const energy = document.createElement('div');
      energy.className = 'glaive-rush-dragon-energy';
      const angle = (i / 10) * 360;
      const radius = 60;
      energy.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      energy.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      energy.style.animationDelay = `${i * 35}ms`;
      container.appendChild(energy);
      elements.push(energy);
    }

    // PHASE 2: Reckless Dragon Charge (450-850ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('glaive-rush-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Dragon energy trail
      for (let i = 0; i < 12; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'glaive-rush-trail';
          const progress = i / 12;
          trail.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 20 - 6}px`;
          trail.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 20 - 6}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 450);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Devastating Dragon Impact (850-1350ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('glaive-rush-opponent-hit');
      playerEl.classList.remove('glaive-rush-user-dash');

      // Dragon explosion
      const explosion = document.createElement('div');
      explosion.className = 'glaive-rush-explosion';
      explosion.style.left = `${opponentX - 110}px`;
      explosion.style.top = `${opponentY - 110}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Dragon energy burst
      for (let i = 0; i < 20; i++) {
        const burstAngle = (i / 20) * 360;
        const burstDist = 60 + Math.random() * 50;
        const burst = document.createElement('div');
        burst.className = 'glaive-rush-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'glaive-rush-shockwave';
          sw.style.left = `${opponentX - 80}px`;
          sw.style.top = `${opponentY - 80}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 850);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Vulnerability Aftermath (1350-1700ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('glaive-rush-user-charge');
      playerEl.classList.add('glaive-rush-user-vulnerable');
    }, 1350);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('glaive-rush-user-charge', 'glaive-rush-user-dash', 'glaive-rush-user-vulnerable');
      opponentEl.classList.remove('glaive-rush-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('glaive-rush-user-charge', 'glaive-rush-user-dash', 'glaive-rush-user-vulnerable');
      opponentEl.classList.remove('glaive-rush-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

GlaiveRushAnimation.moveType = 'dragon';
